import React, { useMemo, useState } from "react";
import type { CalendarEvent } from "@meupedaco/shared";
import { CrudModal } from "../../components/CrudModal";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { PageState } from "../../components/PageState";
import { useAuth } from "../../auth/AuthContext";
import { usePagedQuery } from "../../hooks/usePagedQuery";

const kindOptions = [
  { value: "HOLIDAY", label: "Feriado" },
  { value: "COMMEMORATION", label: "Comemoracao" },
];

const scopeOptions = [
  { value: "NATIONAL", label: "Nacional" },
  { value: "STATE", label: "Estado" },
  { value: "CITY", label: "Cidade" },
  { value: "COMPANY", label: "Empresa" },
];

export const CalendarPage: React.FC = () => {
  const { client, user } = useAuth();
  const canWrite = Boolean(user?.permissions?.includes("CALENDAR_WRITE"));
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterKind, setFilterKind] = useState("");
  const [filterScope, setFilterScope] = useState("");
  const [message, setMessage] = useState<string | null>(null);

  const {
    rows,
    total,
    loading,
    error,
    page,
    take,
    setPage,
    q,
    setQ,
    reload,
  } = usePagedQuery<CalendarEvent>({
    endpoint: "/calendar",
    filters: {
      from: filterFrom || undefined,
      to: filterTo || undefined,
      kind: filterKind || undefined,
      scope: filterScope || undefined,
    },
    orderBy: "date",
    orderDir: "asc",
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit" | "view">("create");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formDate, setFormDate] = useState("");
  const [formName, setFormName] = useState("");
  const [formKind, setFormKind] = useState("HOLIDAY");
  const [formScope, setFormScope] = useState("NATIONAL");
  const [formState, setFormState] = useState("");
  const [formCity, setFormCity] = useState("");
  const [formRecurring, setFormRecurring] = useState(true);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<CalendarEvent | null>(null);

  const resetForm = () => {
    setEditingId(null);
    setFormDate("");
    setFormName("");
    setFormKind("HOLIDAY");
    setFormScope("NATIONAL");
    setFormState("");
    setFormCity("");
    setFormRecurring(true);
  };

  const openModal = (mode: "create" | "edit" | "view", item?: CalendarEvent) => {
    setModalMode(mode);
    if (mode === "create") {
      resetForm();
    } else if (item) {
      setEditingId(item.id);
      setFormDate(item.date.slice(0, 10));
      setFormName(item.name);
      setFormKind(item.kind);
      setFormScope(item.scope);
      setFormState(item.state ?? "");
      setFormCity(item.city ?? "");
      setFormRecurring(item.isRecurringYearly);
    }
    setModalOpen(true);
  };

  const readOnly = modalMode === "view" || !canWrite;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    const payload = {
      date: formDate,
      name: formName,
      kind: formKind,
      scope: formScope,
      state: formState || null,
      city: formCity || null,
      isRecurringYearly: formRecurring,
      isActive: true,
    };
    const response = editingId
      ? await client.put(`/calendar/${editingId}`, payload)
      : await client.post("/calendar", payload);
    if (!response.ok) {
      setMessage("Falha ao salvar evento.");
      return;
    }
    setModalOpen(false);
    resetForm();
    await reload();
  };

  const handleDelete = async () => {
    if (!pendingDelete) {
      return;
    }
    const response = await client.del(`/calendar/${pendingDelete.id}`);
    if (!response.ok) {
      setMessage("Falha ao remover evento.");
      return;
    }
    setConfirmOpen(false);
    setPendingDelete(null);
    await reload();
  };

  const paginationText = useMemo(() => {
    if (total === 0) {
      return "0 resultados";
    }
    const start = (page - 1) * take + 1;
    const end = Math.min(page * take, total);
    return `${start} - ${end} de ${total}`;
  }, [page, take, total]);

  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
        <h1 className="mb-0">Calendario</h1>
        {canWrite && (
          <button className="btn btn-primary" type="button" onClick={() => openModal("create")}>
            Novo evento
          </button>
        )}
      </div>

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">De</label>
              <input
                className="form-control"
                type="date"
                value={filterFrom}
                onChange={(event) => setFilterFrom(event.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Ate</label>
              <input
                className="form-control"
                type="date"
                value={filterTo}
                onChange={(event) => setFilterTo(event.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Tipo</label>
              <select
                className="form-select"
                value={filterKind}
                onChange={(event) => setFilterKind(event.target.value)}
              >
                <option value="">Todos</option>
                {kindOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Escopo</label>
              <select
                className="form-select"
                value={filterScope}
                onChange={(event) => setFilterScope(event.target.value)}
              >
                <option value="">Todos</option>
                {scopeOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Busca</label>
              <input
                className="form-control"
                placeholder="Buscar por nome"
                value={q}
                onChange={(event) => setQ(event.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {message && <div className="alert alert-danger">{message}</div>}

      <div className="card shadow-sm">
        <div className="card-body">
          <PageState loading={loading} error={error}>
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Nome</th>
                    <th>Tipo</th>
                    <th>Escopo</th>
                    <th>Recorrente</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item) => (
                    <tr key={item.id}>
                      <td>{item.date.slice(0, 10)}</td>
                      <td>{item.name}</td>
                      <td>{item.kind === "HOLIDAY" ? "Feriado" : "Comemoracao"}</td>
                      <td>{item.scope}</td>
                      <td>{item.isRecurringYearly ? "Sim" : "Nao"}</td>
                      <td className="text-end">
                        {canWrite ? (
                          <>
                            <button
                              className="btn btn-outline-secondary btn-sm me-2"
                              onClick={() => openModal("edit", item)}
                            >
                              Editar
                            </button>
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => {
                                setPendingDelete(item);
                                setConfirmOpen(true);
                              }}
                            >
                              Remover
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => openModal("view", item)}
                          >
                            Ver
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center text-muted">
                        Nenhum evento encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </PageState>
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">{paginationText}</small>
            <div className="btn-group">
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page <= 1}
                onClick={() => setPage(Math.max(1, page - 1))}
              >
                Anterior
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page * take >= total}
                onClick={() => setPage(page + 1)}
              >
                Proximo
              </button>
            </div>
          </div>
        </div>
      </div>

      <CrudModal
        title={
          modalMode === "create"
            ? "Novo evento"
            : modalMode === "edit"
              ? "Editar evento"
              : "Detalhes do evento"
        }
        open={modalOpen}
        mode={modalMode}
        canWrite={canWrite}
        onClose={() => setModalOpen(false)}
        formId="calendar-form"
      >
        <form id="calendar-form" onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label">Data</label>
            <input
              className="form-control"
              type="date"
              value={formDate}
              onChange={(event) => setFormDate(event.target.value)}
              disabled={readOnly}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Nome</label>
            <input
              className="form-control"
              value={formName}
              onChange={(event) => setFormName(event.target.value)}
              disabled={readOnly}
              required
            />
          </div>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Tipo</label>
              <select
                className="form-select"
                value={formKind}
                onChange={(event) => setFormKind(event.target.value)}
                disabled={readOnly}
              >
                {kindOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Escopo</label>
              <select
                className="form-select"
                value={formScope}
                onChange={(event) => {
                  setFormScope(event.target.value);
                  if (event.target.value !== "STATE") {
                    setFormState("");
                  }
                  if (event.target.value !== "CITY") {
                    setFormCity("");
                  }
                }}
                disabled={readOnly}
              >
                {scopeOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            {(formScope === "STATE" || formScope === "CITY") && (
              <div className="col-md-6">
                <label className="form-label">UF</label>
                <input
                  className="form-control"
                  value={formState}
                  onChange={(event) => setFormState(event.target.value.toUpperCase())}
                  disabled={readOnly}
                />
              </div>
            )}
            {formScope === "CITY" && (
              <div className="col-md-6">
                <label className="form-label">Cidade</label>
                <input
                  className="form-control"
                  value={formCity}
                  onChange={(event) => setFormCity(event.target.value)}
                  disabled={readOnly}
                />
              </div>
            )}
            <div className="col-md-6">
              <div className="form-check mt-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={formRecurring}
                  onChange={(event) => setFormRecurring(event.target.checked)}
                  disabled={readOnly}
                  id="calendar-recurring"
                />
                <label className="form-check-label" htmlFor="calendar-recurring">
                  Recorrente anual
                </label>
              </div>
            </div>
          </div>
        </form>
      </CrudModal>

      <ConfirmDialog
        open={confirmOpen}
        title="Confirmar remocao"
        description="Deseja remover este evento?"
        confirmLabel="Remover"
        cancelLabel="Cancelar"
        danger
        onCancel={() => {
          setConfirmOpen(false);
          setPendingDelete(null);
        }}
        onConfirm={handleDelete}
      />
    </div>
  );
};
